import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import { Grid, Typography, useIsMobile } from '@paintscout/ui'
import React from 'react'
import type { PresentationSectionProps } from '../../../..'

const useStyles = makeStyles<Theme, SampleColors>(
  (theme) => ({
    root: {
      background: theme.palette.common.white,
      boxShadow:
        '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
      overflow: 'hidden'
    },
    exploreExteriorPaint: {
      position: 'relative',
      paddingLeft: '20px',
      paddingRight: '20px'
    },
    exploreExteriorGridWrapper: {
      position: 'relative',
      zIndex: 1,
      textAlign: 'center'
    },
    exploreExteriorPaintTitle: {
      margin: '28px 0',
      fontFamily: 'CustomFont-SW',
      fontSize: '44px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '48px',
      letterSpacing: '-1.5px',
      [theme.breakpoints.down('md')]: {
        padding: '0 15px'
      }
    },
    exploreExteriorPaintLink: {
      color: '#000',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    exploreExteriorPaintLinkText: {
      textAlign: 'left',
      margin: '9px 0',
      fontFamily: 'CustomFont-SW',
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '-0.5px',
      [theme.breakpoints.down('md')]: {
        // textAlign: 'center'
      }
    },
    exploreExteriorPaintDescription: {
      marginBottom: '20px',
      textAlign: 'left',
      fontFamily: 'CustomFont-SW',
      fontSize: '19.5px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '-0.5px'
    },
    exploreExteriorPaintBackground: {
      position: 'absolute',
      zIndex: 0,
      top: 0,
      left: 0,
      width: '100%',
      height: '350px',
      background: '#F6F0E8',
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    contentGridContainer: {
      // justifyContent: 'flex-start',
      // alignItems: 'flex-start',
      // flexDirection: 'column',
      padding: '0 20px',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'center'
      }
    },
    image: {
      width: '100%',
      // maxWidth: '330px',
      [theme.breakpoints.down('md')]: {
        // maxWidth: '300px'
      },
      [theme.breakpoints.down('sm')]: {
        maxWidth: '250px'
      },
      [theme.breakpoints.down(450)]: {
        maxWidth: '90%'
      }
    }
  }),
  { name: 'SampleColors' }
)

export interface SampleColors {
  classes?: StyleClasses<typeof useStyles>
}

function SampleColors(props: PresentationSectionProps) {
  const classes = useStyles(props)
  const isXs = useIsMobile({ custom: 600 })

  return (
    <div className={classes.root}>
      <div className={classes.exploreExteriorPaint}>
        <Grid container className={classes.exploreExteriorGridWrapper}>
          <Grid item xs={12}>
            <Typography variant="h2" className={classes.exploreExteriorPaintTitle}>
              Sample Color in Your Space
            </Typography>
          </Grid>
          <Grid item container spacing={2} justifyContent="center">
            {exploreExteriorPaintItems.map((item) => (
              <Grid item container xs={isXs ? 12 : 4} sm={4} key={item.href} className={classes.contentGridContainer}>
                <Grid item xs={12}>
                  <img src={item.image} className={classes.image} />
                </Grid>
                <Grid item xs={12}>
                  <a href={item.href} className={classes.exploreExteriorPaintLink} target="_blank" rel="noreferrer">
                    <Typography className={classes.exploreExteriorPaintLinkText}>{item.title}</Typography>
                  </a>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" className={classes.exploreExteriorPaintDescription}>
                    {item.description}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default SampleColors

type ExploreExteriorPaintItem = {
  image: string
  title: React.ReactNode
  href: string
  description: React.ReactNode
}

const exploreExteriorPaintItems: ExploreExteriorPaintItem[] = [
  {
    image: 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/peel-and-stick.png',
    title: <>Peel & Stick &gt;</>,
    href: 'https://samples.sherwin-williams.com/view/top-50-colors?_gl=1*ipru9e*_gcl_aw*R0NMLjE3MDk3Mzg0MzAuQ2p3S0NBaUFyZmF1QmhBcEVpd0Flb0I3cUFQSkozVXp1SjF4NlpDNFk1XzBTekJKMTNXcnl4Yy1PM3lMcGE2UXhOSXBmbG1JRl9YRHl4b0M5YXdRQXZEX0J3RQ..*_gcl_au*MTg3MzYxNTQwNy4xNzA1NTEwMjA2*_ga*MTkxMDU0MDU2MS4xNzAxNzIwOTM1*_ga_BFTFS1H2E0*MTcwOTgzMDQ2Ni45NC4xLjE3MDk4MzA1NDEuNTMuMC4w&_ga=2.44348844.941519321.1709664242-1910540561.1701720935&_gac=1.223506281.1709738716.CjwKCAiArfauBhApEiwAeoB7qAPJJ3UzuJ1x6ZC4Y5_0SzBJ13Wryxc-O3yLpa6QxNIpflmIF_XDyxoC9awQAvD_BwE',
    description: <>No mess, no dry time. Try 8" x 8" peel & stick samples.</>
  },
  {
    image: 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/color-to-go.png',
    title: <>Color to Go &gt;</>,
    href: 'https://samples.sherwin-williams.com/view/top-50-colors?_gl=1*ipru9e*_gcl_aw*R0NMLjE3MDk3Mzg0MzAuQ2p3S0NBaUFyZmF1QmhBcEVpd0Flb0I3cUFQSkozVXp1SjF4NlpDNFk1XzBTekJKMTNXcnl4Yy1PM3lMcGE2UXhOSXBmbG1JRl9YRHl4b0M5YXdRQXZEX0J3RQ..*_gcl_au*MTg3MzYxNTQwNy4xNzA1NTEwMjA2*_ga*MTkxMDU0MDU2MS4xNzAxNzIwOTM1*_ga_BFTFS1H2E0*MTcwOTgzMDQ2Ni45NC4xLjE3MDk4MzA1NDEuNTMuMC4w&_ga=2.44348844.941519321.1709664242-1910540561.1701720935&_gac=1.223506281.1709738716.CjwKCAiArfauBhApEiwAeoB7qAPJJ3UzuJ1x6ZC4Y5_0SzBJ13Wryxc-O3yLpa6QxNIpflmIF_XDyxoC9awQAvD_BwE',
    description: <>Preview your paint with Twist-n- Pour Color to Go® samples.</>
  },
  {
    image: 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/color-chips.png',
    title: <>Free Color Chips &gt;</>,
    href: 'https://samples.sherwin-williams.com/view/top-50-colors?_gl=1*ipru9e*_gcl_aw*R0NMLjE3MDk3Mzg0MzAuQ2p3S0NBaUFyZmF1QmhBcEVpd0Flb0I3cUFQSkozVXp1SjF4NlpDNFk1XzBTekJKMTNXcnl4Yy1PM3lMcGE2UXhOSXBmbG1JRl9YRHl4b0M5YXdRQXZEX0J3RQ..*_gcl_au*MTg3MzYxNTQwNy4xNzA1NTEwMjA2*_ga*MTkxMDU0MDU2MS4xNzAxNzIwOTM1*_ga_BFTFS1H2E0*MTcwOTgzMDQ2Ni45NC4xLjE3MDk4MzA1NDEuNTMuMC4w&_ga=2.44348844.941519321.1709664242-1910540561.1701720935&_gac=1.223506281.1709738716.CjwKCAiArfauBhApEiwAeoB7qAPJJ3UzuJ1x6ZC4Y5_0SzBJ13Wryxc-O3yLpa6QxNIpflmIF_XDyxoC9awQAvD_BwE',
    description: <>Get up to ten 2" x 3" color chips delivered, free.</>
  }
]
